import React, {useEffect, useState} from 'react';

import collabShirt from '../assets/images/common/collaboration-shirt.png'

import * as L from "lucid-cardano";
import PageHeader from '../components/page-header';
import CustomCheckbox from '../components/checkbox';
import ConfirmationOrderItem from '../components/confirmation-order-item';
import ButtonOutline from '../components/button-outline';
import ButtonFilled from '../components/button-filled';
import { showToastError } from '../components/toastNotification';
import { useDispatch, useSelector } from 'react-redux';
import { selectOrderInfo, deleteOrderItemByIndex } from '../redux/orderInfoSlice';
import OrderItemDeleteModal from './modals/delete-order-item-modal';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { selectWallet, selectWalletAddress } from '../redux/walletSlice';
import { BrowserWallet } from '@meshsdk/core';
import { selectConfig } from '../redux/configSlice';
import { OpenTxid, PaymentTxid, UserDeadline } from '../constants/localStorageKeys';
import WarningInformation from '../components/warning-information';
import { selectSession } from '../redux/sessionSlice';
import WaitingLoaderHover from './modals/waiting-loader-hover';
import { BackendApi, Socket } from './router-page';

const confirmData = {
  confirmOrder: false,
};

const confirmDataValidationSchema = Yup.object().shape({
  confirmOrder: Yup.bool().oneOf([true], 'You must confirm order'),
});

function tryParseDatum(datum, type) {
  try {
    return L.Data.from(datum, type);
  } catch {
    return null;
  }
}

const ConfirmationCheckoutPage = ({ handleNext, handlePrev, handleGoHomepage }) => {
  const savedShippingInfo = useSelector(selectOrderInfo);
  const dispatch = useDispatch();

  const [isDeleting, setIsDeleting] = useState(false);
  const [itemIndexToDelete, setItemIndexToDelete] = useState(-1);
  const [isContractOpened, setIsContractOpened] = useState(false);
  const walletAddress = useSelector(selectWalletAddress);
  const walletName = useSelector(selectWallet);
  const config = useSelector(selectConfig);
  const [isPaymentStart, setIsPaymentStart] = useState(false);
  const [isPaymentProcessing, setIsPaymentProcessing] = useState(false);
  const [isDelayTimerAction, setIsDelayTimerAction] = useState(false);

  const sessionId = useSelector(selectSession);

  const checkBoxesFormik = useFormik({
    initialValues: confirmData,
    validationSchema: confirmDataValidationSchema,
    validateOnChange: true,
  });

  const purchase = async () => {
    setIsPaymentStart(true);
    const checkboxErrors = await checkBoxesFormik.validateForm(checkBoxesFormik.values);

    if (Object.keys(checkboxErrors).length === 0) {
      const savedDeadline = localStorage.getItem(UserDeadline);

      try {
        const seller = config.inventoryAddress;
        const lucid = window.lucid;
        const address = await lucid.wallet.address();
        const details = L.getAddressDetails(address);
        const hash = details.paymentCredential.hash;
        const sellerDetails = L.getAddressDetails(seller);
        const sellerPkh = sellerDetails.paymentCredential.hash;
  
        const buyerUtxos = await lucid.utxosAt(address);

        const buyerAssets = buyerUtxos.map(x => { 
            return { 
                assets: Object.keys(x.assets).map(key => {
                    const assetClass = L.fromUnit(key);
                    return {
                        policyId: assetClass.policyId,
                    };
                }) 
            };
        });
  
        if (buyerAssets.some(x => x.assets.some(policy => policy.policyId === config.policyId))) {
          showToastError({ title: "Payment failed", body: "You already have some nfts from this collection" });
          return;
        }

        const ContractParams = L.Data.Tuple([L.Data.Bytes()]);
  
        const contractScript = {
          type: "PlutusV2",
          script: L.applyParamsToScript(config.contractScript, [sellerPkh], ContractParams)
        };

        const ContractDatum = L.Data.Object({
          buyer: L.Data.Bytes(),
          collection: L.Data.Bytes(),
          price: L.Data.Integer(),
          deadline: L.Data.Integer(),
          session: L.Data.Bytes(),
        });
  
        const ExchangeRedeemer = L.Data.Enum([
          L.Data.Object({ Pay: L.Data.Tuple([L.Data.Integer()]) }),
          L.Data.Literal("Close")
        ]);
  
        const contractAddress = lucid.utils.validatorToAddress(contractScript);
  
        const utxos = await lucid.utxosAt(contractAddress);
  
        const utxo = utxos
          .map(x => ({ utxo: x, datum: tryParseDatum(x.datum, ContractDatum) }))
          .find(x => x.datum != null && x.datum.deadline > Date.now() && x.datum.buyer == hash && x.datum.session == sessionId);
  
        if (utxo == null) {
          showToastError({ title: "Payment failed", body: "Please, try to purchase again" });
          console.error('NO UTXOS FOUND');
          return;
        }

        const actualItemsAmount = savedShippingInfo.sizes.length;  
        const price = BigInt(config.unitPrice*1000000);
        const count = BigInt(actualItemsAmount);
        const leftovers = BigInt(utxo.utxo.assets.lovelace);
        const validTo = Number(utxo.datum.deadline);

        const input = utxo.utxo;
        const limits = {
          memory: L.PROTOCOL_PARAMETERS_DEFAULT.maxTxExMem,
          cpu: L.PROTOCOL_PARAMETERS_DEFAULT.maxTxExSteps
        }
  
        const datumModel = { 
          buyer: "",
          collection: "",
          price: 0n,
          deadline: BigInt(Date.now() - 200000),
          session: sessionId
        };
        const datum = L.Data.to(datumModel, ContractDatum);

        const value = {
          lovelace: count * price + leftovers
        };

        const assetsToBuy = Object.keys(input.assets).filter(x => x != "lovelace");
        const assetsToIgnore = assetsToBuy.splice(actualItemsAmount);

        for (let asset of assetsToIgnore) {
            value[asset] = 1n;
        }

        const tx = await lucid
          .newTx()
          .collectFrom([input], L.Data.to({ Pay: [count] }, ExchangeRedeemer))
          .payToContract(contractAddress, { inline: datum }, value)
          .validTo(validTo)
          .attachSpendingValidator(contractScript)
          .addSignerKey(hash)
          .complete();
        
        let signed;
        let txId;
        
        signed = await tx.sign().complete();
        if (new Date().getTime() > localStorage.getItem(UserDeadline)) {
          showToastError({ title: "Payment failed", body: "Your time is up" });
          return;
        }
        localStorage.removeItem(UserDeadline);
        setIsDelayTimerAction(true);
        
        txId = await signed.submit();
        
        const assets = assetsToBuy.map(key => {
          const assetClass = L.fromUnit(key);
          return assetClass.assetName ? L.toText(assetClass.assetName) : null;
        }).filter(x => !!x); 
        
        if (txId) {
          const response = await BackendApi.post(
            `/script/complete`,
            {
              nfts: assets,
              sizes: savedShippingInfo.sizes,
              orderInfo: {
                fullName: savedShippingInfo.fullName,
                email: savedShippingInfo.email,
                shippingAddress: savedShippingInfo.shippingAddress,
                shippingAddressCont: savedShippingInfo.shippingAddressCont,
                zipCode: savedShippingInfo.zipCode,
                city: savedShippingInfo.city,
                state: savedShippingInfo.state,
                country: savedShippingInfo.country,
                prefixPhoneNumber: `${savedShippingInfo.prefixPhoneNumber}`,
                phoneNumber: savedShippingInfo.phoneNumber,
                walletName: walletName,
                walletLastChars: walletAddress.slice(-4)
              },
              paymentTxid: txId
            }
          );
  
          if (response.data.success) {
            localStorage.setItem(PaymentTxid, txId);
            setIsPaymentProcessing(true);
          } else {
            localStorage.setItem(UserDeadline, savedDeadline);
            showToastError({ title: "Payment failed", body: response.data.message})
          }
        }
      } catch (err) {
        if (!localStorage.getItem(UserDeadline))
          localStorage.setItem(UserDeadline, savedDeadline);

        console.error("Error on payment", err);

        if (typeof err === 'string') {
          showToastError({ title: "Payment failed", body: "Please make sure you have enough funds in your wallet and try making the purchase again" })
        }
        else {
          showToastError({ title: "Payment failed", body: "You may have declined a payment" });
        }
        
        return;
      }
      finally {
        setIsPaymentStart(false);
        setIsDelayTimerAction(false);
      } 
    }
    else {
      checkBoxesFormik.setTouched(Object.keys(checkBoxesFormik.values).reduce((acc, key) => ({ ...acc, [key]: true }), {}));
      setIsPaymentStart(false);
      return;
    }
  }

  async function isPaymentProcessed() {
    try {
      const response = await BackendApi.get(
        `/script/is-payment-completed`,
        {
          params: {
            openTxid: localStorage.getItem(OpenTxid)
          }
        }
      );
  
      if (response.data) {
        setIsPaymentProcessing(false);
      }
              
    }
    catch {}
  }

  useEffect(() => {
    if (!isPaymentProcessing)
      return;

    return () => { 
      handleNext();
    }
  }, [isPaymentProcessing])

  const openDeleteConfirmation = (itemId) => {
    setIsDeleting(true);
    setItemIndexToDelete(itemId);
  }

  const confirmDelete = async () => {
    dispatch(deleteOrderItemByIndex(itemIndexToDelete));
    setIsDeleting(false);
    setItemIndexToDelete(-1);
    if (savedShippingInfo.sizes.length === 1) {
      handleGoHomepage();
      try {
        await BackendApi.post(
          `/script/cancel`,
          {
            buyer: walletAddress
          }
        );
      }
      catch{}
    }
  }

  const cancelDelete = () => {
    setIsDeleting(false);
    setItemIndexToDelete(-1);
  }

  async function isSmartContractActive() {
    try {      
      const response = await BackendApi.get(
        `/script/opened`,
        {
          params: {
            openTxid: localStorage.getItem(OpenTxid) 
          }
        }
      );

      if (response.data) {
        setIsContractOpened(true);
        return true;
      }
    }
    catch {
      return false;
    }
  }

  useEffect(() => {

    const paymentTxId = localStorage.getItem(PaymentTxid); 
    if (paymentTxId) {
      setIsPaymentProcessing(true);
      isPaymentProcessed();
    }

    const initContractInfo = async () => {
      await isSmartContractActive();
    };
  
    initContractInfo();

    Socket.on("paymentWaiting", () => setIsContractOpened(true));
    Socket.on("paymentReceived", () => setIsPaymentProcessing(false));

    return () => {
      Socket.off("paymentWaiting");
      Socket.off("paymentReceived");
    };
  }, []);

  return (
    <section className='page-container'>
      <div className='order-page confirmation-stage'>

        <div className='center-items-horizontal'>
          <PageHeader currentStepNumber={4} backToHomepage={handleGoHomepage} isDelayTimerAction={isDelayTimerAction}/>

          <div className='confirmation-check-details'>
            <p>Please go over all details to confirm your purchase</p>

            <div className='shipping-info'>
              <div className='block-header'>
                <h4>SHIPPING INFO</h4>
                <ButtonOutline label={'EDIT'} palette='secondary' size='small' customStyles={{width: 77}} onClick={handlePrev}/>
              </div>
              <div className='content'>
                <p>Recipent’s Full Name: {savedShippingInfo.fullName ? savedShippingInfo.fullName : 'Mooki Cardano'}</p>
                <p>Email: {savedShippingInfo.email ? savedShippingInfo.email : 'Mooki@Lvlcs.io'}</p>
                <p>Shipping Address: {savedShippingInfo.shippingAddress ? savedShippingInfo.shippingAddress : '1234 S. Main St.'}</p>
                <p>Shipping Address Cont: {savedShippingInfo.shippingAddressCont ? savedShippingInfo.shippingAddressCont : ''}</p>
                <div className='row-multiple-values'>
                  <p>Zip Code: {savedShippingInfo.zipCode ? savedShippingInfo.zipCode : '75555'}</p>
                  <p>City: {savedShippingInfo.city ? savedShippingInfo.city : 'Tulsa'}</p>
                </div>
                <p>State: {savedShippingInfo.state ? savedShippingInfo.state : 'Oklahoma'}</p>
                <p>Country: {savedShippingInfo.country ? savedShippingInfo.country : 'United States'}</p>
                <p>Telephone: {savedShippingInfo.prefixPhoneNumber ? `${savedShippingInfo.prefixPhoneNumber}` : '+1-'}{savedShippingInfo.phoneNumber ? savedShippingInfo.phoneNumber : '918-555-1100'}</p>
              </div>
            </div>

            <div className='product-info'>
              <div className='block-header product-header'>
                <h4>PRODUCT INFO</h4>
                <ButtonOutline label={'EDIT SIZE'} palette='secondary' size='small' customStyles={{width: 95}} onClick={handlePrev}/>
              </div>
              <div className='content'>
                {savedShippingInfo.sizes.map((value, index) => (
                  <>
                    <ConfirmationOrderItem itemImg={collabShirt} itemName={'LVLCS x World Mobile Token Football Jersey'} itemSize={value} itemPrice={config.unitPrice} isDeletable={true} handleDeleteClick={() => openDeleteConfirmation(index)}  />
                    <ConfirmationOrderItem itemImg={collabShirt} itemName={'LVLCS x World Mobile Token Football Jersey NFT'} itemPrice={'0'} />
                  </>
                ))}
                {/* <ConfirmationOrderItem itemImg={collabShirt} itemName={'5,000 Pointz Tokens'} itemPrice={'0'} /> */}
              </div>
            </div>

            <div className='total-sum-info'>
              <div className='block-header'>
                <h4>TOTAL</h4>
              </div>

              <div className='row-multiple-content'>
                <p>Wallet: {walletName} - XXXXXXXXXXXXXXXX{walletAddress ? walletAddress.slice(-4) : 'XXXX'}</p>
                <p className='price'>Total: {config.unitPrice * savedShippingInfo.sizes.length} ADA</p>
              </div>
            </div>

            <CustomCheckbox label={<p>I have confirmed that all the info in checkout section is accurate and I am aware that I can no longer make changes to my sizes or shipping informations after I checkout.</p>}
              checked={checkBoxesFormik.values.confirmOrder} onChange={(e) => { checkBoxesFormik.handleChange(e); checkBoxesFormik.setFieldTouched("confirmOrder", false)}} name="confirmOrder"
              isStatusDisplayed={!!checkBoxesFormik.touched.confirmOrder} error={checkBoxesFormik.errors.confirmOrder}/>

            {!isContractOpened && <WarningInformation information={'Please wait a moment until everything is ready to complete your purchase'}/>}
          </div>
        </div>

        <div className='navigation-block-buttons'>
          <ButtonFilled label='BACK' palette='secondary' size='regular' customStyles={{width: 219}} onClick={handlePrev}/>
          <ButtonFilled label='PURCHASE' palette='success' size='regular' customStyles={{width: 219}} onClick={purchase} disabled={!isContractOpened}/>
        </div>
      </div>

      <OrderItemDeleteModal isOpen={isDeleting} onCancel={cancelDelete} 
        onConfirm={() => { confirmDelete() }} 
        bodyText={savedShippingInfo.sizes.length > 1 ? "Are you sure you want to delete this product?" : "Do you want to cancel your order?"}/>

      <WaitingLoaderHover isOpen={isPaymentStart || isPaymentProcessing} text={isPaymentProcessing ? <h4>Payment is processing <br/>It can takes up to the 3 minutes</h4> : ""} />
    </section>
  );
};

export default ConfirmationCheckoutPage;